import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TraditionalRecipes = () => {
  const [selectedRecipe, setSelectedRecipe] = useState(0);

  const recipes = [
  {
    name: "Nonna\'s Cornetti",
    category: "Breakfast Pastry",
    origin: "Northern Italy",
    description: "Our signature cornetti recipe has been passed down through four generations. The secret lies in the lamination technique—folding butter into the dough 27 times to create those delicate, flaky layers. We proof the dough overnight at precisely 4°C, allowing the flavors to develop fully. Each morning, we hand-roll and shape every cornetto, filling them with our homemade crema pasticcera made from fresh eggs, vanilla beans imported from Madagascar, and organic milk from local Ontario farms.",
    image: "https://images.unsplash.com/photo-1652283319954-0535ab575f06",
    imageAlt: "Golden flaky Italian cornetti pastries arranged on rustic wooden board with powdered sugar dusting, showing delicate layered texture and crescent shape, photographed in warm natural morning light",
    culturalSignificance: "In Italian culture, cornetti represent the start of a new day—a moment of simple pleasure before the day's work begins. Unlike French croissants, Italian cornetti are slightly sweeter and softer, meant to be enjoyed with cappuccino in the morning ritual.",
    keyIngredients: ["Italian 00 Flour", "European Butter", "Fresh Eggs", "Madagascar Vanilla", "Organic Milk"],
    technique: "27-fold lamination with overnight cold fermentation",
    icon: "Croissant"
  },
  {
    name: "Focaccia Genovese",
    category: "Artisan Bread",
    origin: "Genoa, Italy",
    description: "This isn't just bread—it's a piece of Ligurian history. Our focaccia follows the traditional Genovese method, using a biga (pre-ferment) that develops for 16 hours. The dough is enriched with extra virgin olive oil from our family's grove in Liguria, creating that signature golden crust and pillowy interior. We dimple the surface by hand, creating little pools for the olive oil and sea salt to settle. The result is a focaccia with a crispy bottom, soft center, and that unmistakable olive oil aroma.",
    image: "https://images.unsplash.com/photo-1714667533821-f19187cd8fc3",
    imageAlt: "Traditional Italian focaccia bread with characteristic dimpled surface glistening with olive oil, topped with fresh rosemary sprigs and coarse sea salt, displayed on rustic wooden cutting board in artisan bakery setting",
    culturalSignificance: "Focaccia has been a staple in Genoa since the Middle Ages. Traditionally eaten as a snack or light meal, it represents the Italian philosophy of using simple, quality ingredients to create something extraordinary.",
    keyIngredients: ["Ligurian Olive Oil", "Sea Salt from Trapani", "Fresh Rosemary", "Italian Flour", "Natural Yeast"],
    technique: "16-hour biga fermentation with hand-dimpling",
    icon: "Wheat"
  },
  {
    name: "Panettone Tradizionale",
    category: "Holiday Specialty",
    origin: "Milan, Italy",
    description: "Our panettone is a labor of love that takes three days to create. We use a natural yeast starter (lievito madre) that we've been maintaining since 1985—the same culture Giuseppe brought from Italy. The dough undergoes three separate rises, developing complex flavors and that characteristic light, airy texture. We fold in candied orange peel from Sicily, raisins soaked in Marsala wine, and Madagascar vanilla. Each panettone is baked in traditional paper molds and hung upside down to cool, preventing collapse and ensuring perfect structure.",
    image: "https://images.unsplash.com/photo-1605949790413-068f4b35ba52",
    imageAlt: "Tall dome-shaped Italian panettone Christmas bread with golden brown crust studded with candied fruits and raisins, sliced to reveal light fluffy interior texture, presented on elegant serving platter with festive holiday decorations",
    culturalSignificance: "Panettone is the crown jewel of Italian Christmas traditions. In Milan, families gather on Christmas Eve to share panettone with sweet wine, symbolizing prosperity and togetherness for the coming year.",
    keyIngredients: ["40-year Lievito Madre", "Sicilian Candied Orange", "Marsala-soaked Raisins", "Fresh Butter", "Egg Yolks"],
    technique: "3-day natural fermentation with inverted cooling",
    icon: "Cake"
  },
  {
    name: "Biscotti di Prato",
    category: "Traditional Cookie",
    origin: "Tuscany, Italy",
    description: "These twice-baked almond cookies have been a Tuscan tradition since the Renaissance. Our recipe comes from Maria's grandmother in Prato, using whole almonds from Sicily that we toast in-house to bring out their natural oils. The dough is shaped into logs, baked once, then sliced and baked again to achieve that perfect crunch. We add a hint of anise and orange zest, creating layers of flavor that pair beautifully with espresso or Vin Santo dessert wine.",
    image: "https://images.unsplash.com/photo-1591608501532-a982bebd87d5",
    imageAlt: "Rustic Italian biscotti cookies with visible whole almonds arranged on ceramic plate, showing characteristic twice-baked golden texture and diagonal sliced shape, accompanied by small cup of espresso in traditional Italian cafe setting",
    culturalSignificance: "Biscotti were originally created for long journeys—their dry texture meant they wouldn't spoil. Today, they're an essential part of Italian coffee culture, meant to be dunked in espresso or sweet wine.",
    keyIngredients: ["Sicilian Almonds", "Anise Seeds", "Orange Zest", "Italian Flour", "Free-range Eggs"],
    technique: "Double-baking method for perfect crunch",
    icon: "Cookie"
  }];


  return (
    <section className="py-20 bg-gradient-to-b from-card to-background">
      <div className="container-custom">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Icon name="BookOpen" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Heritage Recipes</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
            Traditional Italian Recipes
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Authentic recipes passed down through generations, each with its own story and cultural significance
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-4 mb-8">
          {recipes?.map((recipe, index) =>
          <button
            key={index}
            onClick={() => setSelectedRecipe(index)}
            className={`p-6 rounded-xl text-left transition-all duration-300 ${
            selectedRecipe === index ?
            'bg-primary text-primary-foreground shadow-warm-lg' :
            'bg-card hover:bg-muted'}`
            }>

              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              selectedRecipe === index ? 'bg-primary-foreground/20' : 'bg-primary/10'}`
              }>
                  <Icon
                  name={recipe?.icon}
                  size={24}
                  color={selectedRecipe === index ? 'var(--color-primary-foreground)' : 'var(--color-primary)'} />

                </div>
                <div>
                  <h3 className={`font-serif text-lg font-semibold ${
                selectedRecipe === index ? 'text-primary-foreground' : 'text-foreground'}`
                }>
                    {recipe?.name}
                  </h3>
                  <p className={`text-sm ${
                selectedRecipe === index ? 'text-primary-foreground/80' : 'text-muted-foreground'}`
                }>
                    {recipe?.category}
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="bg-card rounded-2xl shadow-warm-lg overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden">
              <Image
                src={recipes?.[selectedRecipe]?.image}
                alt={recipes?.[selectedRecipe]?.imageAlt}
                className="w-full h-full object-cover" />

              <div className="absolute top-6 left-6">
                <div className="px-4 py-2 rounded-full bg-background/90 backdrop-blur-sm border border-border flex items-center gap-2">
                  <Icon name="MapPin" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">
                    {recipes?.[selectedRecipe]?.origin}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name={recipes?.[selectedRecipe]?.icon} size={32} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="font-serif text-3xl font-bold text-foreground">
                    {recipes?.[selectedRecipe]?.name}
                  </h3>
                  <p className="text-muted-foreground">
                    {recipes?.[selectedRecipe]?.category}
                  </p>
                </div>
              </div>

              <p className="text-foreground leading-relaxed mb-6">
                {recipes?.[selectedRecipe]?.description}
              </p>

              <div className="bg-accent/10 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Sparkles" size={20} color="var(--color-accent)" />
                  <h4 className="font-semibold text-foreground">Cultural Significance</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {recipes?.[selectedRecipe]?.culturalSignificance}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Leaf" size={18} color="var(--color-primary)" />
                    <h4 className="font-semibold text-foreground text-sm">Key Ingredients</h4>
                  </div>
                  <ul className="space-y-2">
                    {recipes?.[selectedRecipe]?.keyIngredients?.map((ingredient, idx) =>
                    <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {ingredient}
                      </li>
                    )}
                  </ul>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="ChefHat" size={18} color="var(--color-primary)" />
                    <h4 className="font-semibold text-foreground text-sm">Technique</h4>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {recipes?.[selectedRecipe]?.technique}
                  </p>
                </div>
              </div>

              <Button variant="default" iconName="Heart" iconPosition="left" fullWidth>
                Try This Recipe
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TraditionalRecipes;